import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { toast } from "sonner";
import { Trash2, Pencil, Save, X as XIcon } from "lucide-react";

export default function BankAccountsPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("banks", "write");
    const { entities, currencies } = useAppCtx();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ entity_id: "", name: "", account_number: "", currency: "USD", opening_balance: 0 });
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const startEdit = (b) => {
        setEditingId(b.id);
        setEditForm({
            entity_id: b.entity_id,
            name: b.name,
            account_number: b.account_number || "",
            currency: b.currency,
            opening_balance: b.opening_balance,
        });
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };
    const saveEdit = async (id) => {
        try {
            await api.put(`/banks/${id}`, {
                ...editForm,
                opening_balance: parseFloat(editForm.opening_balance || 0),
            });
            toast.success("Bank account updated");
            setEditingId(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const load = async () => {
        const { data } = await api.get("/banks");
        setRows(data);
    };
    useEffect(() => {
        load();
    }, []);

    const submit = async () => {
        if (!form.entity_id || !form.name || !form.currency) {
            toast.error("Entity, name and currency are required");
            return;
        }
        try {
            await api.post("/banks", { ...form, opening_balance: parseFloat(form.opening_balance || 0) });
            toast.success("Bank account created");
            setOpen(false);
            setForm({ entity_id: "", name: "", account_number: "", currency: "USD", opening_balance: 0 });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete bank account?")) return;
        await api.delete(`/banks/${id}`);
        await load();
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    return (
        <div data-testid="banks-page">
            <PageHeader
                title="Bank Accounts"
                eyebrow="Masters"
                description="Each entity may hold multiple bank accounts, one default currency per account."
                action={
                    canWrite ? (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="bank-add-btn">
                                    + New bank account
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm" data-testid="bank-dialog">
                                <DialogHeader>
                                    <DialogTitle>Create bank account</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <Field label="Entity">
                                        <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="bank-entity-select">
                                                <SelectValue placeholder="Select entity" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {entities.map((e) => (
                                                    <SelectItem key={e.id} value={e.id}>
                                                        {e.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Bank / Account name">
                                        <Input
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="rounded-sm h-9"
                                            data-testid="bank-name-input"
                                        />
                                    </Field>
                                    <Field label="Account number">
                                        <Input
                                            value={form.account_number}
                                            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                                            className="rounded-sm h-9 font-mono"
                                            data-testid="bank-acctnum-input"
                                        />
                                    </Field>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field label="Default currency">
                                            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                                <SelectTrigger className="rounded-sm" data-testid="bank-currency-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {currencies.map((c) => (
                                                        <SelectItem key={c.code} value={c.code}>
                                                            {c.code} — {c.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Opening balance">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={form.opening_balance}
                                                onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                                                className="rounded-sm h-9 font-mono"
                                                data-testid="bank-opening-input"
                                            />
                                        </Field>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="bank-submit-btn">
                                        Create
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    ) : null
                }
            />

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px] financial">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Entity</th>
                            <th className="px-4 py-2.5">Bank</th>
                            <th className="px-4 py-2.5">Account #</th>
                            <th className="px-4 py-2.5">Currency</th>
                            <th className="px-4 py-2.5 num">Opening Balance</th>
                            <th className="px-4 py-2.5 w-32 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-slate-500 text-[13px] py-10">
                                    No bank accounts yet
                                </td>
                            </tr>
                        )}
                        {rows.map((b) =>
                            editingId === b.id ? (
                                <tr key={b.id} className="border-b border-slate-100 bg-blue-50/30" data-testid={`bank-edit-row-${b.id}`}>
                                    <td className="px-4 py-2">
                                        <Select value={editForm.entity_id} onValueChange={(v) => setEditForm({ ...editForm, entity_id: v })}>
                                            <SelectTrigger className="h-8 rounded-sm" data-testid={`bank-edit-entity-${b.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-8 rounded-sm" data-testid={`bank-edit-name-${b.id}`} />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input value={editForm.account_number} onChange={(e) => setEditForm({ ...editForm, account_number: e.target.value })} className="h-8 rounded-sm font-mono" data-testid={`bank-edit-acctnum-${b.id}`} />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                                            <SelectTrigger className="h-8 rounded-sm font-mono" data-testid={`bank-edit-currency-${b.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input type="number" step="0.01" value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: e.target.value })} className="h-8 rounded-sm font-mono text-right" data-testid={`bank-edit-opening-${b.id}`} />
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                        <Button size="sm" onClick={() => saveEdit(b.id)} className="h-7 px-2 rounded-sm bg-[#002FA7]" data-testid={`bank-edit-save-${b.id}`}>
                                            <Save size={12} />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 px-2 ml-1" data-testid={`bank-edit-cancel-${b.id}`}>
                                            <XIcon size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            ) : (
                                <tr key={b.id} className="border-b border-slate-100" data-testid={`bank-row-${b.id}`}>
                                    <td className="px-4 py-2">{entityName(b.entity_id)}</td>
                                    <td className="px-4 py-2">{b.name}</td>
                                    <td className="px-4 py-2 font-mono">{b.account_number || "—"}</td>
                                    <td className="px-4 py-2 font-mono">{b.currency}</td>
                                    <td className="px-4 py-2 num">{Number(b.opening_balance).toFixed(2)}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                        {canWrite && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => startEdit(b)}
                                                    className="h-7 px-2"
                                                    data-testid={`bank-edit-${b.id}`}
                                                >
                                                    <Pencil size={13} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => remove(b.id)}
                                                    className="h-7 px-2 text-red-600"
                                                    data-testid={`bank-delete-${b.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-600 mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
